import React, { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDrop } from 'react-dnd';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { Box, useMediaQuery, Theme } from '@mui/material';
import { IconDocuments } from '@maiia/design-system';
import { Button, cn } from '@kiiwi/ui';
import OpenTokContext from './context';
import { useTranslation } from '@/src/i18n';
import { APP_ROUTES } from '@/src/constants';
import WaitingCard from './WaitingCard/WaitingCard';
import { checkVideoStatusIs } from './utils';
import { VideoSessionRequiredWithCardHcd } from '@/src/types/videoSession';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';

/*
 * The component is use to display video session preview in other page when user switch to other page when their in video session
 * This look like floating video
 *
 * Check if router is not tlcSession
 * Check if status is WAITING or STARTED
 * if WAITING show small waiting
 * if STARTED expand it and show video preview
 */
const MiniOpenTok = () => {
  const opentokCommonRef = React.useRef<HTMLDivElement | null>(null);
  const ctx = React.useContext(OpenTokContext);
  const videoSession = useSelector<
    any,
    VideoSessionRequiredWithCardHcd | undefined
  >((state: any) => state?.videoSessions?.item);
  const videoSessionStatus = videoSession?.videoSessionStatus;
  const videoSessionWaiting = checkVideoStatusIs(videoSessionStatus, [
    'PENDING',
    'WAITING',
  ]);
  const videoSessionStarted = videoSessionStatus === 'STARTED';
  const router = useRouter();
  const { t } = useTranslation();
  const isShow =
    ![APP_ROUTES.TLC.SESSION_ID, APP_ROUTES.TLC.TLC].includes(
      router.pathname,
    ) &&
    checkVideoStatusIs(videoSessionStatus, ['WAITING', 'PENDING', 'STARTED']);
  const [dragState, setDragState] = React.useState<{
    position: any;
    isDragging: boolean;
  }>({
    position: {},
    isDragging: false,
  });
  // Handle for move MiniTok
  const miniTokRef = React.useRef<HTMLDivElement | null>(null);

  const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  const onMouseDown = React.useCallback(e => {
    const evt = e || window.event;
    evt.preventDefault();
    setDragState({
      position: { x: evt.clientX, y: evt.clientY },
      isDragging: true,
    });
  }, []);
  React.useEffect(() => {
    const onMouseUp = () => {
      setDragState(oldState => ({ ...oldState, isDragging: false }));
    };
    const onMouseMove = e => {
      const evt = e || window.event;
      evt.preventDefault();
      const translation = {
        x: evt.clientX - dragState.position.x,
        y: evt.clientY - dragState.position.y,
      };
      // set the element's new position:
      if (miniTokRef.current)
        miniTokRef.current.style.transform = `translate(${translation.x}px, ${translation.y}px)`;
    };

    if (dragState.isDragging) {
      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('contextmenu', onMouseUp);
      return () => {
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('contextmenu', onMouseUp);
        if (miniTokRef.current && miniTokRef.current.style?.transform !== '') {
          const matches = miniTokRef.current.style.transform.match(/\((.*)\)/);
          if (!matches) return;
          const [x, y] = matches[1].split(',').map(val => parseFloat(val));
          miniTokRef.current.style.left = `${miniTokRef.current.offsetLeft +
            x}px`;
          miniTokRef.current.style.top = `${miniTokRef.current.offsetTop +
            y}px`;
          miniTokRef.current.style.right = 'inherit';
          miniTokRef.current.style.transform = '';
        }
      };
    }
  }, [dragState.isDragging]);

  // Handle switch to mobile view
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  useIsomorphicLayoutEffect(() => {
    if (miniTokRef.current && isMobile) {
      miniTokRef.current.style.left = '';
      miniTokRef.current.style.top = '';
    }
  }, [isMobile]);
  // End handle switch to mobile view

  // Handle for drop Document
  const [{ canDrop }, drop] = useDrop({
    accept: 'DOCUMENT',
    drop: () => ({
      practitionerId: videoSession?.practitionerId,
      centerId: videoSession?.centerId,
    }),
    collect: monitor => ({
      canDrop: monitor.canDrop(),
    }),
  });

  // Handle for append opentokcommon to minitok
  useIsomorphicLayoutEffect(() => {
    if (
      !(
        ctx.showVideoSession &&
        ctx.showMiniOpenTok &&
        videoSessionStatus === 'STARTED'
      )
    )
      return;

    opentokCommonRef.current?.appendChild(ctx.container.current);

    return () => {
      ctx.parentContainer.current.appendChild(ctx.container.current);
    };
  }, [ctx.showVideoSession, ctx.showMiniOpenTok, videoSessionStatus]);

  if (!(isShow && videoSession)) return null;

  return (
    <>
      <Box
        sx={{ boxShadow: 2 }}
        className={cn(
          'mini-open-tok group',
          'flex flex-col items-center justify-center bg-white sm:w-[250px]',
          canDrop && 'is-dropping',
        )}
        ref={miniTokRef}
      >
        <div
          className="moveHandler"
          onMouseDown={!isMobile ? onMouseDown : undefined}
        />
        <div ref={drop} className={clsx('dropOverlay', canDrop && 'active')}>
          <div className="placeholder">
            <IconDocuments />
          </div>
        </div>

        {videoSessionWaiting ? (
          <WaitingCard
            cardHcd={videoSession?.cardHcd}
            isAmcTlc={videoSession?.subType === 'TLC_AMC'}
            hideDelay
            hideAvatar
            videoSessionId={videoSession.id}
            isMiniOpenTok
          />
        ) : (
          <div className="opentok-common" ref={opentokCommonRef} />
        )}
        {(videoSessionStarted || videoSessionWaiting) && (
          <div
            className={cn(
              'relative mb-2',
              videoSessionStarted && 'absolute mb-0',
            )}
          >
            <Button
              size="sm"
              variant="primary"
              color="macro"
              data-cy={
                videoSessionStarted
                  ? 'btn-minitok-goto-video'
                  : 'btn-minitok-waiting-room'
              }
              onClick={() =>
                router.push({
                  pathname: APP_ROUTES.TLC.SESSION_ID,
                  query: { id: videoSession.id },
                })
              }
              className={cn(
                'relative z-[9999]',
                videoSessionStarted && 'hidden group-hover:inline-flex',
              )}
            >
              {videoSessionStarted
                ? t('join-video-session')
                : t('join-wainting-room')}
              <FontAwesomeIcon name="circle-play:regular" className="ml-2" />
            </Button>
          </div>
        )}
      </Box>
    </>
  );
};

export default MiniOpenTok;
